import React, { useEffect, useState } from 'react';
import { Paperclip, Send, CheckCheck, ShieldCheck, CornerDownLeft } from 'lucide-react';
import { composerEmail, dnsChecks, siteConfig } from '../data/mock';
import { Avatar } from './Brand';

const EmailComposer = () => {
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('typing');

  useEffect(() => {
    let i = 0;
    let timer;
    const type = () => {
      if (i <= composerEmail.body.length) {
        setTyped(composerEmail.body.slice(0, i));
        i += 1;
        timer = setTimeout(type, 18);
      } else {
        timer = setTimeout(() => setPhase('sent'), 500);
      }
    };
    const start = () => { i = 0; setTyped(''); setPhase('typing'); type(); };
    start();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (phase === 'sent') {
      const t1 = setTimeout(() => setPhase('reply'), 1400);
      return () => clearTimeout(t1);
    }
  }, [phase]);

  return (
    <div className="relative sm:pb-0" data-testid="hero-composer">
      <div className="window">
        <div className="window-bar">
          <span className="window-dot" /><span className="window-dot" /><span className="window-dot" />
          <span className="ml-3 font-mono text-[11px] text-slate-2 tracking-wider">NEW MESSAGE<span className="hidden sm:inline"> · SEQUENCE 1 / STEP 1</span></span>
          <span className="ml-auto pill pill-green"><ShieldCheck className="w-3 h-3" /> AUTH OK</span>
        </div>

        <div className="px-5 sm:px-6 pt-4 text-sm">
          <div className="flex items-center gap-3 py-2.5 border-b border-[#E2E8F0]">
            <span className="w-14 text-slate-2">From</span>
            <Avatar size="w-6 h-6" />
            <span className="text-ink font-medium">{siteConfig.personName}</span>
            <span className="text-slate-2 hidden sm:inline">&lt;usama@outreachbyusama.com&gt;</span>
          </div>
          <div className="flex items-center gap-3 py-2.5 border-b border-[#E2E8F0]">
            <span className="w-14 text-slate-2">To</span>
            <span className="pill">{composerEmail.to}</span>
          </div>
          <div className="flex items-center gap-3 py-2.5 border-b border-[#E2E8F0]">
            <span className="w-14 text-slate-2">Subject</span>
            <span className="text-ink font-medium truncate">{composerEmail.subject}</span>
          </div>
        </div>

        <div className="px-5 sm:px-6 py-5 min-h-[190px]">
          <p className={`text-[15px] leading-relaxed text-ink whitespace-pre-line ${phase === 'typing' ? 'caret' : ''}`} data-testid="composer-body">
            {typed}
          </p>
        </div>

        <div className="px-5 sm:px-6 pb-5 flex flex-wrap items-center gap-2">
          {dnsChecks.map((d) => (
            <span key={d.label} className="pill pill-green" title={d.value} data-testid={`dns-chip-${d.label.toLowerCase()}`}>
              <CheckCheck className="w-3 h-3" /> {d.label}
            </span>
          ))}
          <span className="ml-auto flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-slate-2" />
            <span className={`btn-ink !h-9 !px-4 !rounded-lg text-xs ${phase !== 'typing' ? '!bg-[#10B981]' : ''}`}>
              {phase === 'typing' ? <><Send className="w-3.5 h-3.5" /> Sending…</> : <><CheckCheck className="w-3.5 h-3.5" /> Delivered</>}
            </span>
          </span>
        </div>
      </div>

      {phase !== 'typing' && (
        <div className="pop-in mt-4 sm:mt-0 sm:absolute sm:-bottom-7 sm:-left-6 panel px-4 py-3 flex items-center gap-3 shadow-xl bg-white" data-testid="composer-delivered-toast">
          <span className="relative w-9 h-9 rounded-full bg-[#ECFDF5] flex items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-[#10B981] ping-soft opacity-30" />
            <CheckCheck className="relative w-4 h-4 text-green" />
          </span>
          <div className="leading-tight">
            <p className="text-sm font-semibold text-ink">Delivered · Primary inbox</p>
            <p className="text-[11px] text-slate-2 font-mono">not promotions · not spam</p>
          </div>
        </div>
      )}

      {phase === 'reply' && (
        <div className="pop-in mt-3 sm:mt-0 sm:absolute sm:-bottom-7 sm:right-4 panel px-4 py-3 sm:max-w-[280px] shadow-xl border-l-4 !border-l-[#10B981] bg-white" data-testid="composer-reply-toast">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-7 h-7 rounded-full bg-ink text-white text-xs font-bold flex items-center justify-center">S</span>
            <p className="text-xs font-semibold text-ink">Sarah replied <span className="text-slate-2 font-normal">· just now</span></p>
            <CornerDownLeft className="w-3.5 h-3.5 text-green ml-auto" />
          </div>
          <p className="text-sm text-ink leading-snug">"{composerEmail.reply}"</p>
        </div>
      )}
    </div>
  );
};

export default EmailComposer;
